import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  ArrowRight, ArrowLeft, User, UserPlus, Ticket, 
  AlertCircle, CheckCircle, DollarSign, Clock
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const TRANSFER_FEE = 20;

const BusTicketTransfer = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [step, setStep] = useState(1); // 1: select passenger, 2: enter new details, 3: confirm
  const [transfer, setTransfer] = useState(null);
  
  const [newPassenger, setNewPassenger] = useState({
    name: '',
    phone: '',
    id_number: '',
    reason: ''
  });

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await api.get('/bus/bookings/my');
      const found = response.data.bookings?.find(b => b.id === bookingId);
      
      if (!found) {
        toast({
          title: 'Error',
          description: 'Booking not found',
          variant: 'destructive'
        });
        navigate('/bus/tickets');
        return;
      }
      
      if (found.status === 'cancelled' || found.status === 'completed') {
        toast({
          title: 'Cannot Transfer',
          description: 'This ticket cannot be transferred',
          variant: 'destructive'
        });
        navigate('/bus/tickets');
        return;
      }
      
      setBooking(found);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load booking',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateTransfer = async () => {
    if (!newPassenger.name || !newPassenger.phone) {
      toast({
        title: 'Missing Information',
        description: 'Please enter the new passenger name and phone',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/bus/transfer/initiate', {
        booking_id: bookingId,
        passenger_index: selectedPassenger,
        new_passenger_name: newPassenger.name,
        new_passenger_phone: newPassenger.phone,
        new_passenger_id_number: newPassenger.id_number || null,
        reason: newPassenger.reason || null
      });
      
      setTransfer(response.data);
      setStep(3);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initiate transfer',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!transfer?.transfer_id) return;

    setSubmitting(true);
    try {
      await api.post(`/bus/transfer/confirm/${transfer.transfer_id}`);
      
      toast({
        title: 'Transfer Complete!',
        description: 'The ticket has been transferred successfully',
      });
      
      navigate('/bus/tickets');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to confirm transfer',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTransfer = async () => {
    if (!transfer?.transfer_id) {
      setStep(1);
      return;
    }

    try {
      await api.post(`/bus/transfer/cancel/${transfer.transfer_id}`);
      toast({
        title: 'Transfer Cancelled',
        description: 'The transfer has been cancelled',
      });
      navigate('/bus/tickets');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel transfer',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bus/tickets')}>
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Transfer Ticket</h1>
              <p className="text-blue-100 text-sm">Change passenger details</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Passenger */}
        {step === 1 && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">Select Passenger to Transfer</h2>
            
            <div className="space-y-3 mb-6">
              {booking?.passengers?.map((passenger, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedPassenger(idx)}
                  disabled={passenger.transferred}
                  className={`w-full p-4 rounded-lg border-2 flex items-center justify-between transition-all ${
                    passenger.transferred 
                      ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                      : selectedPassenger === idx
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <User className={`h-5 w-5 ${
                      selectedPassenger === idx ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <div className="text-left">
                      <p className="font-medium">{passenger.name}</p>
                      <p className="text-sm text-gray-500">Seat {passenger.seat}</p>
                    </div>
                  </div>
                  {passenger.transferred ? (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      Already Transferred
                    </span>
                  ) : selectedPassenger === idx && (
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  )}
                </button>
              ))}
            </div>

            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={selectedPassenger === null}
              onClick={() => setStep(2)}
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Card>
        )}

        {/* Step 2: Enter New Passenger Details */}
        {step === 2 && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4">New Passenger Details</h2>
            
            {/* Current Passenger */}
            <div className="p-4 bg-gray-50 rounded-lg mb-6">
              <p className="text-sm text-gray-500 mb-1">Transferring from:</p>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-gray-400" />
                <span className="font-medium">{booking?.passengers?.[selectedPassenger]?.name}</span>
                <span className="text-sm text-gray-500">
                  (Seat {booking?.passengers?.[selectedPassenger]?.seat})
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>New Passenger Name *</Label>
                <Input
                  value={newPassenger.name}
                  onChange={(e) => setNewPassenger({ ...newPassenger, name: e.target.value })}
                  placeholder="Full name as on ID"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newPassenger.phone}
                  onChange={(e) => setNewPassenger({ ...newPassenger, phone: e.target.value })}
                  placeholder="+260..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label>ID Number (Optional)</Label>
                <Input
                  value={newPassenger.id_number}
                  onChange={(e) => setNewPassenger({ ...newPassenger, id_number: e.target.value })}
                  placeholder="NRC or Passport"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Reason for Transfer (Optional)</Label>
                <Textarea
                  value={newPassenger.reason}
                  onChange={(e) => setNewPassenger({ ...newPassenger, reason: e.target.value })}
                  placeholder="e.g., Change of travel plans"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Transfer Fee Notice */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-start gap-2">
                <DollarSign className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800">Transfer Fee: K{TRANSFER_FEE}</p>
                  <p className="text-sm text-yellow-700">
                    A transfer fee applies to change passenger details.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleInitiateTransfer}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Continue'}
              </Button>
            </div>
          </Card>
        )}

        {/* Step 3: Confirm Transfer */}
        {step === 3 && transfer && (
          <Card className="p-6">
            <div className="text-center mb-6">
              <UserPlus className="h-12 w-12 text-blue-600 mx-auto mb-3" />
              <h2 className="text-lg font-semibold">Confirm Transfer</h2>
              <p className="text-gray-500">Review and confirm the transfer details</p>
            </div>

            {/* Transfer Summary */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-gray-500">From</p>
                  <p className="font-medium">{transfer.transfer?.originalPassenger?.name}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400" />
                <div className="flex-1 text-right">
                  <p className="text-sm text-gray-500">To</p>
                  <p className="font-medium">{transfer.transfer?.newPassenger?.name}</p>
                </div>
              </div>

              <div className="flex justify-between p-4 bg-blue-50 rounded-lg">
                <span className="text-blue-700">Transfer Fee</span>
                <span className="font-bold text-blue-700">K{transfer.fee}</span>
              </div>
            </div>

            {/* Time Notice */}
            <div className="p-4 bg-yellow-50 rounded-lg mb-6">
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-700">
                  Transfers must be completed at least 2 hours before departure.
                  The new passenger will receive their ticket details via SMS.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={handleCancelTransfer}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleConfirmTransfer}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : `Pay K${transfer.fee} & Confirm`}
              </Button>
            </div>
          </Card>
        )}

        {/* Info Card */}
        <Card className="mt-4 p-4 bg-blue-50 border-blue-100">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">Transfer Policy</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Transfers must be done at least 2 hours before departure</li>
                <li>A K{TRANSFER_FEE} fee applies per transfer</li>
                <li>The seat assignment remains the same</li>
                <li>Each passenger can only be transferred once</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BusTicketTransfer;
